import React, { useState, useEffect } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { Dropdown } from 'antd'

import classNames from 'classnames'

import { getUniqueKey } from 'utils'

import { isEmail, addSelectionChangeEvent, removeSelectionChangeEvent } from 'utils/ui'

import NodeHelper, { def, parseRFC822Email } from './nodeHelper'

import styles from './style.module.scss'

const {
  tokenSeparators,
  editAreaClsName,
  itemClsName,
  emptyClsName,
  emailClsName,
  notEmailClsName,
  innerEmailClsName,
  outerEmailClsName,
  zwsp,
  blockEditable,
} = def

interface UserInfo {
  name?: string
  email?: string
}

interface EmailSelectProps extends Partial<WithTranslation> {
  innerRef?: React.MutableRefObject<any>
  onChange?: (refObj: any, userInfo: UserInfo, isDelete: boolean) => void
  search?: (text: string, exactEmail?: boolean) => Promise<any[]>
  placeholder?: string
  overlayClassName?: string
  innerDomain?: string
  initialFocus?: boolean
  maxCount?: number
  dataTestId?: string
}

interface EmailSelectState {
  hasText: boolean
}

interface ElemObj {
  ref: HTMLElement | null
  id?: string
  needFocus?: boolean
  clsNames: string[]
  text?: string
  userInfo?: UserInfo
  elem?: React.ReactElement
  cursorPos?: number | null
  setClsNames?: (newClsName: string) => void
  setFixed?: (value: boolean) => void
  setMenu?: (menu: any[]) => void
  setMenuOpen?: (value: boolean) => void
  closeMenu?: () => boolean
  moveFocus?: (moveUp: boolean, keyEvent: any) => void
  setText?: (txt: string, pos?: number) => void
  setPosition?: (pos: number) => void
  checkEmailType?: (bySelect?: boolean, emailTxt?: string, nameTxt?: string) => void
  onSelect?: (key: string) => void
  checkEnter?: () => boolean
}

class EmailSelect extends React.Component<EmailSelectProps, EmailSelectState> {
  state: EmailSelectState = {
    hasText: false,
  }

  ret: any = null

  isInitialized: boolean = false

  count: number = 0

  elems: ElemObj[] = []

  key: string = 'init'

  menuCls: string | null = null

  hasFocus: boolean = false

  isBlockSearch: boolean = true

  _isMounted: boolean = false

  uniqueId: string = getUniqueKey('emailSelect')

  ref: HTMLElement | null = null

  refObj: any = null

  searchResult: any[] = []

  checkMenuTimer: any = null

  pasteAddedList: string[] = []
  pasteRemovedList: string[] = []

  componentDidMount() {
    const { innerRef } = this.props

    this._isMounted = true

    this.refObj = this.createRefObj()

    if (innerRef != null) {
      innerRef.current = this.refObj
    }

    addSelectionChangeEvent(this.onSelectionChange)

    setTimeout(() => {
      this.isBlockSearch = false
    }, 100)
  }

  // componentDidUpdate(prevProps) {
  // }

  componentWillUnmount() {
    const { innerRef } = this.props

    this._isMounted = false

    removeSelectionChangeEvent(this.onSelectionChange)

    if (innerRef != null) {
      innerRef.current = null
    }
  }

  createRefObj = () => {
    const obj = {
      getRef: () => {
        return this.ref
      },
      focus: () => {
        setTimeout(() => {
          if (this.ref != null) {
            this.ref.focus()
          }
        }, 20)
      },
      getUserInfoList: (skipEditing?: boolean) => {
        return this.getUserInfoList(skipEditing)
      },
      getValidUserInfoList: (skipEditing?: boolean) => {
        return this.getValidUserInfoList(skipEditing)
      },
      getEmailList: (/*skipEditing*/) => {
        return this.getEmailList()
      },
      getValidEmailList: (/* skipEditing */) => {
        return this.getValidEmailList()
      },
      setText: (text: string) => {
        console.log(' -- setText -- ', text)
        if (this.ref != null) {
          this.ref.innerHTML = text

          this.flatten(true)
        }
      },
      setEmailList: (list: string[]) => {
        console.log(' -- setEmailList -- 1', list)
        if (this.ref != null) {
          const text = list.join('; ')
          this.ref.textContent = text

          this.flatten(true)

          setTimeout(() => {
            this.checkTextEmpty()
          }, 20)
        }
      },
      setUserInfoList: (list: UserInfo[]) => {
        console.log(' -- setUserList -- 2', list)
        if (this.ref != null) {
          this.elems = []
          let nodeObj: ElemObj | null = null
          list
            .map((item) => {
              const { name, email } = item
              let text = ''
              if (name != null && email != null) {
                text = `"${name}" <${email}>`
              } else if (email != null || name != null) {
                text = email || name
              }
              return text
            })
            .forEach((text) => {
              nodeObj = this.createNewMailItem(zwsp, emptyClsName)
              this.insertBeforeElemObj(nodeObj, null)
              const newMailItem = this.createNewMailItem(text, emailClsName)
              this.insertBeforeElemObj(newMailItem, null)
            })

          nodeObj = this.createNewMailItem(zwsp, emptyClsName, 1)
          this.insertBeforeElemObj(nodeObj, null, true)

          setTimeout(() => {
            this.checkTextEmpty()

            if (this.elems.length > 0) {
              const lastElem = this.elems[this.elems.length - 1]
              if (lastElem.ref != null) {
                this.setCursorPosition(lastElem.ref, 1, true)
              }
            }
          }, 20)
        }
      },
      addEmailList: (list: string[]) => {
        console.log(' -- addEmailList --', list)
        const emailList = this.getEmailList()
        const newEmailList = [...emailList]
        list.forEach((emailTxt) => {
          if (emailList.indexOf(emailTxt) === -1) {
            newEmailList.push(emailTxt)
          }
        })
        obj.setEmailList(newEmailList)
      },
      addUserInfoList: (list: UserInfo[]) => {
        console.log(' -- addUserInfoList --', list)
        const userInfoList = this.getUserInfoList()
        const newUserInfoList = [...userInfoList]
        list.forEach((userInfo) => {
          const existInfo = userInfoList.find((info) => {
            return info.email === userInfo.email
          })
          if (existInfo == null) {
            newUserInfoList.push(userInfo)
          }
        })
        obj.setUserInfoList(newUserInfoList)
      },
    }

    return obj
  }

  getUserInfoFromElem = (elem: HTMLElement): UserInfo => {
    const userInfo: UserInfo = {}
    const email = elem.getAttribute('data-email')
    const name = elem.getAttribute('data-name')

    if (email != null) {
      userInfo.email = email
    }
    if (name != null) {
      userInfo.name = name
    }

    if (email == null && name == null) {
      let text = elem.textContent || ''
      text = text.replace(/\u200B/g, '')

      userInfo.name = text
    }

    return userInfo
  }

  parseEmailElems = (skipEditing: boolean = true): UserInfo[] => {
    const userInfoList = this.elems
      .map((elem) => {
        let userInfo: UserInfo | null = null
        if (elem.clsNames.indexOf(emailClsName) > -1) {
          userInfo = elem.userInfo || null
        }
        if (userInfo == null && elem.ref != null) {
          if (elem.ref.classList.contains('fixed') === true) {
            const name = elem.ref.getAttribute('data-name')
            const email = elem.ref.getAttribute('data-email')
            userInfo = { name: name || undefined, email: email || undefined }
          }
          if (userInfo == null && skipEditing === false) {
            const text = elem.ref.textContent
            if (text != null && text.trim().length > 5) {
              userInfo = this.parseNodeText(text)
              console.log(' --- parseNodeText -- ', userInfo)
            }
          }
        }
        return userInfo
      })
      .filter((userInfo): userInfo is UserInfo => userInfo != null)

    return userInfoList
  }

  getUserInfoList = (skipEditing?: boolean): UserInfo[] => {
    const userInfoList = this.parseEmailElems(skipEditing)
    return userInfoList
  }

  getValidUserInfoList = (skipEditing?: boolean): UserInfo[] => {
    const userInfoList = this.parseEmailElems(skipEditing)
    const validList = userInfoList.filter((item) => {
      return item.email != null
    })
    return validList
  }

  getEmailList = (skipEditing?: boolean): string[] => {
    const userInfoList = this.getUserInfoList(skipEditing)
    const emailList = userInfoList.map((userInfo) => {
      return userInfo.email || userInfo.name || ''
    })
    return emailList
  }

  getValidEmailList = (skipEditing?: boolean): string[] => {
    const userInfoList = this.getValidUserInfoList(skipEditing)
    const emailList = userInfoList.map((userInfo) => {
      return userInfo.email || ''
    })
    return emailList
  }

  setRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      this.ref = ref
      if (this.isInitialized === false) {
        this.isInitialized = true
        this.init()
      }
    }
  }

  init = () => {
    if (this.ref != null) {
      const newMailItem = this.createNewMailItem(zwsp)

      // this.elems.push(newMailItem);

      this.insertBeforeElemObj(newMailItem, null)

      // this.ref.appendChild(newMailItem);
      // this.setCursorPosition(newMailItem.firstChild, 1);
      this.ref.focus()
    }
  }

  notifyChanged = (userInfo: UserInfo, isDelete?: boolean) => {
    const { onChange } = this.props
    console.log(' -- notifyChanged -- ', userInfo, isDelete === true)

    setTimeout(() => {
      if (onChange != null) {
        onChange(this.refObj, userInfo, isDelete === true)
      }

      this.checkTextEmpty()
    }, 20)
  }

  insertBeforeElemObj = (elemObj: ElemObj, container: HTMLElement | null, update?: boolean) => {
    if (container != null) {
      const containerIdx = this.getContainerIdx(container)
      if (containerIdx < 0) {
        this.elems.push(elemObj)
      } else {
        this.elems.splice(containerIdx, 0, elemObj)
      }
    } else {
      this.elems.push(elemObj)
    }

    if (update !== false) {
      this.forceUpdate()
    }
  }

  getContainerIdx = (container: HTMLElement | null): number => {
    if (container == null) {
      return -1
    }
    const containerIdx = this.elems.findIndex((elem) => {
      return elem.id === container.getAttribute('objid')
    })
    return containerIdx
  }

  removeElemObjByContainer = (container: HTMLElement, update?: boolean) => {
    const containerIdx = this.getContainerIdx(container)
    if (containerIdx > -1) {
      this.elems.splice(containerIdx, 1)

      const prevNode = container.previousSibling as HTMLElement | null

      if (prevNode != null && prevNode.classList.contains(emptyClsName) === true) {
        // 중복되는 zwsp 노드를 삭제.
        let nextNode = container.nextSibling as HTMLElement | null

        while (
          nextNode != null &&
          nextNode.classList.contains(emptyClsName) === true &&
          nextNode.textContent === zwsp
        ) {
          const idx = this.getContainerIdx(nextNode)
          if (idx > -1) {
            this.elems.splice(idx, 1)
          }
          nextNode = nextNode.nextSibling as HTMLElement | null
        }

        if (
          nextNode != null &&
          nextNode.classList.contains(emptyClsName) === true &&
          nextNode.textContent !== zwsp
        ) {
          if (prevNode.textContent === zwsp) {
            const idx = this.getContainerIdx(prevNode)
            if (idx > -1) {
              this.elems.splice(idx, 1)
            }
            this.setCursorPosition(nextNode.firstChild as Node, 1)
          } else {
            const pos = prevNode.textContent!.length
            const text = prevNode.textContent + nextNode.textContent!.substring(1)
            this.setContainerText(prevNode, text, pos)
            const idx = this.getContainerIdx(nextNode)
            if (idx > -1) {
              this.elems.splice(idx, 1)
            }
          }
        } else {
          this.setCursorPosition(prevNode.firstChild as Node, prevNode.textContent!.length)
        }
      }

      const { classList } = container
      let userInfo: UserInfo | null = null
      if (classList.contains(itemClsName) && classList.contains(emailClsName)) {
        userInfo = this.getUserInfoFromElem(container)
      } else {
        const selector = `.${itemClsName}.${emailClsName}`
        const elem = container.querySelector(selector) as HTMLElement | null
        if (elem) {
          userInfo = this.getUserInfoFromElem(elem)
        }
      }

      this.notifyChanged(userInfo || {}, true)
    }

    if (update !== false) {
      this.forceUpdate()
    }
  }

  setContainerClsName = (container: HTMLElement, clsName: string) => {
    const containerIdx = this.getContainerIdx(container)
    if (containerIdx > -1 && this.elems[containerIdx].setClsNames) {
      this.elems[containerIdx].setClsNames!(clsName)
    }
  }

  checkEdiable = (container: HTMLElement | null, emailTxt?: string): boolean => {
    if (container != null) {
      const containerIdx = this.getContainerIdx(container)
      if (containerIdx > -1) {
        const obj = this.elems[containerIdx]
        if (obj != null && obj.ref != null) {
          let fixed = true
          const email = obj.ref.getAttribute('data-email')
          if (email == null) {
            const text = emailTxt != null ? emailTxt : obj.ref.textContent || ''
            const typeStr = this.getTypeStr(text)
            if (typeStr === notEmailClsName) {
              fixed = false
            }
          }
          if (fixed === true) {
            if (obj.setFixed != null) {
              obj.setFixed(true)
            }
          }
          return fixed
        }
      }
    }
    return false
  }

  // text가 dom에 전달되지 않은 상태인 경우, text를 직접 전달해야 함. - emailTxt
  checkEmailType = (
    container?: HTMLElement | null,
    emailTxt?: string,
    nameTxt?: string,
    bySelect: boolean = false,
  ) => {
    if (container != null) {
      const containerIdx = this.getContainerIdx(container)
      if (containerIdx > -1) {
        if (this.elems[containerIdx].checkEmailType != null) {
          if (emailTxt != null || nameTxt != null) {
            this.elems[containerIdx].checkEmailType!(bySelect, emailTxt, nameTxt)
          } else {
            const info = this.parseNodeText(container.textContent || '')
            this.elems[containerIdx].checkEmailType!(bySelect, info.email, info.name)
          }
        }
      }
    } else {
      this.elems.forEach((elem) => {
        if (elem.checkEmailType != null && elem.clsNames.indexOf(emailClsName) > -1) {
          elem.checkEmailType(bySelect)
        }
      })
    }
  }

  parseNodeText = (text: string): UserInfo => {
    return parseRFC822Email(text)
  }

  setContainerPopup = (container: HTMLElement, open: boolean) => {
    const containerIdx = this.getContainerIdx(container)
    if (containerIdx > -1) {
      if (open === true) {
        this.elems[containerIdx].setMenu!(this.searchResult)
        this.elems.forEach((elem, idx) => {
          elem.setMenuOpen!(containerIdx === idx)
        })
      } else {
        this.elems[containerIdx].setMenuOpen!(false)
      }
    }
  }

  setContainerText = (container: HTMLElement, text: string, pos?: number) => {
    const containerIdx = this.getContainerIdx(container)
    if (containerIdx > -1) {
      this.elems[containerIdx].setText!(text, pos)
    }
  }

  checkSelectKey = (e: React.KeyboardEvent) => {
    let isMenuAction = false
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && selection.isCollapsed === true) {
      const range = selection.getRangeAt(0)
      console.log(' -- checkSelectKey -- ', range)

      const node = range.startContainer
      const container = this.getItemElem(node)
      const containerIdx = this.getContainerIdx(container)
      if (containerIdx > -1) {
        // if (this.elems[containerIdx].closeMenu() === true) {
        isMenuAction = this.elems[containerIdx].checkEnter!()
        if (isMenuAction === true) {
          e.preventDefault()
          e.stopPropagation()
        } else {
          const { keyCode } = e.nativeEvent
          if (
            keyCode === 9 &&
            container &&
            container.classList.contains(emptyClsName) === true &&
            container.textContent!.length > 1
          ) {
            // tab key.
            this.seperateToken(null)
            e.preventDefault()
            e.stopPropagation()
          }
        }
      }
    }
  }

  checkEnter = (e: React.KeyboardEvent) => {
    let isMenuAction = false
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      console.log(' -- range -- ', range)
      if (range.collapsed === true) {
        const container = this.getItemElem(range.startContainer, range.startOffset)
        const containerIdx = this.getContainerIdx(container)
        if (containerIdx > -1) {
          isMenuAction = this.elems[containerIdx].checkEnter!()
        }
      }
    }

    if (isMenuAction !== true) {
      this.seperateToken(null)
    }

    e.preventDefault()
    e.stopPropagation()
  }

  checkBackspace = (e: React.KeyboardEvent) => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = window.getSelection()!.getRangeAt(0)
      console.log(' -- checkBackspace -- ', range)
      if (range.collapsed !== true) {
        // @todo - 선택 영역이 있는 경우, 블럭 단위로 지워지도록 처리 필요.
        return
      } else {
        if (this.ref!.textContent === zwsp) {
          e.preventDefault()
          e.stopPropagation()
          return
        }

        let node = range.startContainer
        let container: Node | HTMLElement = node
        if (container.nodeType !== Node.ELEMENT_NODE) {
          container = container.parentElement!
        }

        if ((container as HTMLElement).classList.contains(emptyClsName) === true) {
          if (
            (container as HTMLElement).textContent!.indexOf(zwsp) === 0 &&
            (range.startOffset === 0 || range.startOffset === 1)
          ) {
            const prevTextNode = NodeHelper.getPrevTextNode(container as HTMLElement)
            if (prevTextNode != null) {
              const txt = prevTextNode.textContent || ''
              if (txt.length > 2 && blockEditable === true) {
                const newLen = txt.length - 1
                // prevTextNode.nodeValue = txt.substring(0, txt.length - 1);
                this.setContainerText(prevTextNode.parentElement!, txt.substring(0, newLen), newLen)
                // this.setCursorPosition(prevTextNode, newLen);
              } else {
                const prevContainer = prevTextNode.parentElement
                this.removeElemObjByContainer(prevContainer!, false)
                // prevContainer.parentElement.removeChild(prevContainer);

                this.forceUpdate()
              }
            }
            e.preventDefault()
            e.stopPropagation()
          }
          // else {
          //   this.checkEmailType(container)
          // }
        }
      }
    }
  }

  movePreviousCursor = (container: HTMLElement): boolean => {
    let prevElem = container.previousSibling as HTMLElement | null
    while (prevElem != null && prevElem.getAttribute('contentEditable') === 'false') {
      prevElem = prevElem.previousSibling as HTMLElement | null
    }
    if (
      prevElem != null &&
      (prevElem as any).objInfo != null &&
      prevElem.getAttribute('contentEditable') !== 'false'
    ) {
      const len = prevElem.textContent!.length
      ;(prevElem as any).objInfo.setPosition(len)
      return true
    }

    return false
  }

  moveNextCursor = (container: HTMLElement): boolean => {
    let nextElem = container.nextSibling as HTMLElement | null
    while (nextElem != null && nextElem.getAttribute('contentEditable') === 'false') {
      nextElem = nextElem.nextSibling as HTMLElement | null
    }
    if (
      nextElem != null &&
      (nextElem as any).objInfo != null &&
      nextElem.getAttribute('contentEditable') !== 'false'
    ) {
      ;(nextElem as any).objInfo.setPosition(1)
      return true
    }

    return false
  }

  moveLeft = (e: React.KeyboardEvent) => {
    if (blockEditable === false) {
      const range = window.getSelection()!.getRangeAt(0)
      if (range.collapsed === true) {
        let node = range.startContainer
        let container: Node | HTMLElement = node
        if (container.nodeType !== Node.ELEMENT_NODE) {
          container = container.parentElement!
        }

        if ((container as HTMLElement).classList.contains(emptyClsName) === true) {
          if (
            (container as HTMLElement).previousSibling != null &&
            (container as HTMLElement).textContent!.indexOf(zwsp) === 0 &&
            range.startOffset === 1
          ) {
            if (this.movePreviousCursor(container as HTMLElement) === true) {
              e.preventDefault()
            }
          }
        }
      }
    }
  }

  moveRight = (e: React.KeyboardEvent) => {
    if (blockEditable === false) {
      const range = window.getSelection()!.getRangeAt(0)
      if (range.collapsed === true) {
        let node = range.startContainer
        let container: Node | HTMLElement = node
        if (container.nodeType !== Node.ELEMENT_NODE) {
          container = container.parentElement!
        }

        if ((container as HTMLElement).classList.contains(emptyClsName) === true) {
          if (
            (container as HTMLElement).nextSibling != null &&
            (container as HTMLElement).textContent!.length === 1 &&
            (container as HTMLElement).textContent!.indexOf(zwsp) === 0 &&
            range.startOffset === 0
          ) {
            if (this.moveNextCursor(container as HTMLElement) === true) {
              e.preventDefault()
            }
          }
        }
      }
    }
  }

  processCopy = (e: React.ClipboardEvent, isCut: boolean) => {
    const selection = document.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (range.collapsed !== true) {
        let elems: HTMLElement[] = []
        let startNode = range.startContainer as Node
        if (startNode.nodeType !== 1) {
          startNode = startNode.parentElement!
        }
        let endNode = range.endContainer as Node
        if (endNode.nodeType !== 1) {
          endNode = endNode.parentElement!
        }
        if (startNode !== endNode) {
          const selector = `.${itemClsName}`
          const startElem =
            (startNode as HTMLElement).querySelector(selector) ||
            (startNode as HTMLElement).closest(selector)
          const endElem =
            (endNode as HTMLElement).querySelector(selector) ||
            (endNode as HTMLElement).closest(selector)
          let elem = startElem as HTMLElement | null
          while (elem != null) {
            if (elem.classList.contains(emailClsName) === true) {
              elems.push(elem)
            }
            if (elem === endElem) {
              break
            }
            elem = elem.nextElementSibling as HTMLElement | null
          }
        } else {
          const selector = `.${itemClsName}`
          const nodeElems = (startNode as HTMLElement).querySelectorAll(selector)
          if (nodeElems == null || nodeElems.length === 0) {
            const elem = (startNode as HTMLElement).closest(selector) as HTMLElement | null
            if (elem != null) {
              elems = [elem]
            }
          } else {
            elems = Array.from(nodeElems) as HTMLElement[]
          }
        }

        const data = NodeHelper.getFlatText(elems)
        const htmlData = data.replace(/[\u00A0-\u9999<>\&]/gim, function (i) {
          return '&#' + i.charCodeAt(0) + ';'
        })

        e.clipboardData.setData('text/plain', data)
        e.clipboardData.setData('text/html', htmlData)

        if (isCut === true) {
          selection.deleteFromDocument()

          this.flatten(false)
        }
      }
    }
  }

  flatten = (cursorToLast: boolean) => {
    const prevEmailList = this.getValidEmailList()
    if (this.ref != null) {
      const res = NodeHelper.flatten(
        this.ref,
        this.createNewMailItem,
        this.elems as any,
        cursorToLast,
      )
      if (res != null) {
        this.key = this.getId()
        this.elems = res.items as any
        this.checkEmailType()
        this.forceUpdate()
      }
    }
    const nextEmailList = this.getValidEmailList()

    const addList = [...nextEmailList]
    const removeList = [...prevEmailList]

    prevEmailList.forEach((email) => {
      const pos = addList.indexOf(email)
      if (pos > -1) {
        addList.splice(pos, 1)
      }
    })

    nextEmailList.forEach((email) => {
      const pos = removeList.indexOf(email)
      if (pos > -1) {
        removeList.splice(pos, 1)
      }
    })

    this.pasteAddedList = [...addList]
    this.pasteRemovedList = [...removeList]
  }

  getItemElem = (node: Node, _offset?: number): HTMLElement | null => {
    let container: Node | HTMLElement = node
    if (container.nodeType !== Node.ELEMENT_NODE) {
      container = container.parentElement!
    }
    if ((container as HTMLElement).classList.contains(itemClsName) === true) {
      return container as HTMLElement
    }
    const owner = (container as HTMLElement).closest(`.${itemClsName}`) as HTMLElement | null
    if (owner != null) {
      const ownerText = owner.textContent || ''
      if ((container as HTMLElement).nextSibling == null) {
        this.setContainerText(owner, ownerText, ownerText.length)
      } else {
        this.setContainerText(owner, ownerText)
      }
    }
    return owner
  }

  checkClose = (e: MouseEvent) => {
    const { target, offsetX, offsetY } = e

    if (target != null && (target as HTMLElement).classList.contains(emailClsName) === true) {
      const top = 8
      const right = (target as HTMLElement).offsetWidth - 9
      const left = right - 18
      const bottom = top + 18

      if (left < offsetX && offsetX < right && top < offsetY && offsetY < bottom) {
        console.log(' -- close -- ', target)
        this.removeElemObjByContainer(target as HTMLElement, true)
      }
    }
  }

  convertToMailItemOnSelect = (container: HTMLElement, emailTxt?: string, nameTxt?: string) => {
    if (container.classList.contains(emptyClsName) === true) {
      const zwspNodeObj = this.createNewMailItem(zwsp, emptyClsName)
      this.insertBeforeElemObj(zwspNodeObj, container, false)

      this.setContainerClsName(container, emailClsName) // email UI로 표시.
      this.checkEmailType(container, emailTxt, nameTxt, true)
      this.checkEdiable(container, emailTxt)

      const newTextNode = this.createNewMailItem(zwsp, emptyClsName, 1)
      this.insertBeforeElemObj(newTextNode, container.nextSibling as HTMLElement | null, true)
    } else {
      this.checkEmailType(container, emailTxt, nameTxt, true)
      if (this.checkEdiable(container, emailTxt) === true) {
        this.moveNextCursor(container)
      }
    }
  }

  seperateToken = (token: string | null) => {
    const range = window.getSelection()!.getRangeAt(0)
    console.log(' -- range -- ', range)
    if (range.collapsed !== true) {
      return
    }

    let node = range.startContainer

    const text = node.textContent || ''

    if (text === zwsp) {
      // 자를 수 없는 문자열이므로 무시.
      return
    }

    let container: Node | HTMLElement = node
    if (container.nodeType !== Node.ELEMENT_NODE) {
      container = container.parentElement!
    }

    const textArr: string[] = []

    let tokenLength = 0
    if (token != null && token.length > 0) {
      tokenLength = token.length
      let comStr = text.substring(range.startOffset - tokenLength, range.startOffset)
      if (comStr.length === 1 && comStr.charCodeAt(0) === 160) {
        comStr = ' '
      }
      if (comStr !== token) {
        return
      }
    }

    const firstText = text.substring(0, range.startOffset - tokenLength)
    textArr.push(firstText)
    const nextText = text.substring(range.startOffset)
    if (nextText.length > 0) {
      textArr.push(nextText)
    }

    const newStr = textArr[0].replace(/\u200B/g, '')

    // node.nodeValue = zwsp + newStr;

    if (newStr.length === 0) {
      if (textArr.length === 1) {
        this.setContainerText(container as HTMLElement, zwsp, 1)
      } else {
        this.setContainerText(container as HTMLElement, zwsp + textArr[1], 1)
      }
      return // 자를 수 있는 문자열이 없음.
    } else {
      this.setContainerText(container as HTMLElement, zwsp + newStr)
    }

    if ((container as HTMLElement).classList.contains(emptyClsName) === true) {
      const zwspNodeObj = this.createNewMailItem(zwsp, emptyClsName)
      this.insertBeforeElemObj(zwspNodeObj, container as HTMLElement, false)

      this.setContainerClsName(container as HTMLElement, emailClsName) // email UI로 표시.
      this.checkEmailType(container as HTMLElement)
      this.checkEdiable(container as HTMLElement)

      let newTextNode: ElemObj | null = null
      if (textArr.length > 1 && textArr[1].length > 0) {
        newTextNode = this.createNewMailItem(zwsp + textArr[1], emptyClsName, 1)
      } else {
        newTextNode = this.createNewMailItem(zwsp, emptyClsName, 1)
      }

      this.insertBeforeElemObj(
        newTextNode,
        (container as HTMLElement).nextSibling as HTMLElement | null,
        true,
      )
    } else {
      this.checkEmailType(container as HTMLElement)
      this.checkEdiable(container as HTMLElement)

      let newTextNode: ElemObj | null = null
      if (textArr.length > 1 && textArr[1].length > 0) {
        const zwspNodeObj = this.createNewMailItem(zwsp, emptyClsName, 1)
        this.insertBeforeElemObj(
          zwspNodeObj,
          (container as HTMLElement).nextSibling as HTMLElement | null,
          false,
        )

        newTextNode = this.createNewMailItem(zwsp + textArr[1], emailClsName)
        this.insertBeforeElemObj(
          newTextNode,
          (container as HTMLElement).nextSibling as HTMLElement | null,
          true,
        )
      } else if (
        (container as HTMLElement).nextSibling != null &&
        ((container as HTMLElement).nextSibling as any).objInfo != null
      ) {
        ;((container as HTMLElement).nextSibling as any).objInfo.setPosition(1)
      }
    }

    const containerIdx = this.getContainerIdx(container as HTMLElement)
    if (containerIdx > -1) {
      this.elems[containerIdx].closeMenu!()
    }
  }

  checkMenu = (container: HTMLElement) => {
    const { initialFocus } = this.props

    if (this.hasFocus !== true || (initialFocus === false && this.isBlockSearch === true)) {
      return
    }

    if (this.checkMenuTimer != null) {
      clearTimeout(this.checkMenuTimer)
    }

    this.checkMenuTimer = setTimeout(() => {
      this.innerCheckMenu(container)
      this.checkMenuTimer = null
    }, 250)
  }

  innerCheckMenu = (container: HTMLElement) => {
    let text = container.textContent || ''

    while (text.indexOf(zwsp) === 0) {
      text = text.substring(1)
    }
    text = text.trim()
    if (text.length < 1) {
      this.setContainerPopup(container, false)
      return
    }
    this.searchEmail(text).then((res) => {
      if (res != null && res.length > 0) {
        this.setContainerPopup(container, true)
      } else {
        this.setContainerPopup(container, false)
      }
    })
  }

  searchEmail = (text: string, exactEmail?: boolean): Promise<any[]> => {
    const { search } = this.props

    console.log(' -- searchEmail -- ', text)

    if (search != null) {
      const promise = new Promise<any[]>((resolve, reject) => {
        try {
          search(text, exactEmail).then((ret) => {
            if (ret == null || Array.isArray(ret) === true) {
              this.searchResult = ret || []
              resolve(ret)
            } else {
              reject(new Error('Invalid search result'))
            }
          })
        } catch (error) {
          reject(error)
        }
      })

      return promise
    }

    return Promise.resolve([])
  }

  onDivInput = (e: InputEvent) => {
    const { data, inputType } = e
    if (data != null && tokenSeparators.indexOf(data) > -1) {
      if (data.charCodeAt(0) === 32 || data.charCodeAt(0) === 160) {
        // space가 입력되는 경우, code가 달라짐. 160 => 32로 변환.
        // this.seperateToken(' ')
        return // space 키 입력은 구분자로 처리하지 않음. paste 시에만 처리.
      } else {
        this.seperateToken(data)
      }
      return
    }

    // if (inputType === 'insertFromPaste' || inputType === 'deleteByCut') {
    if (inputType === 'insertFromPaste') {
      this.flatten(false)
    }

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      console.log(' -- range -- ', range)
      if (range.collapsed === true) {
        const container = this.getItemElem(range.startContainer, range.startOffset)

        if (container != null) {
          this.checkMenu(container)

          if (container.classList.contains(emailClsName) === true) {
            if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
              if (container.textContent == zwsp) {
                const prev = container.previousSibling as HTMLElement | null
                const nextText =
                  container.nextSibling != null
                    ? (container.nextSibling as HTMLElement).textContent || ''
                    : ''
                this.removeElemObjByContainer(container, true)
                if (nextText.length < 2 && prev != null && (prev as any).objInfo != null) {
                  const len = prev.textContent!.length
                  ;(prev as any).objInfo.setPosition(len)
                }
              }
            }

            if ((container as any).objInfo != null) {
              ;(container as any).objInfo.checkEmailType()
            }
          }
          // else {
          //   if (inputType === 'deleteContentForward') {
          //     if (container.textContent == zwsp) {
          //       e.preventDefault();
          //     }
          //   }
          // }
        } else {
          if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
            if (this.ref != null) {
              const text = this.ref.textContent
              if (text == null || text.length === 0) {
                this.flatten(true)
              }
              setTimeout(() => {
                if (this.ref != null) {
                  this.ref.focus()
                }
              }, 100)
            }
          }
        }

        // const rng = NodeHelper.saveRange();
        // this.setContainerText(container, container.textContent);
        // if (rng != null) {
        //   NodeHelper.restoreRange(rng);
        // }
      }
    }

    // else if (data == null && e.inputType === 'insertText') {
    //   console.log(' -- new line -- ', data);
    //   this.seperateToken(null);
    // } else if (data == null && e.inputType === 'deleteContentBackward') {
    //   console.log(' -- back key -- ', data);
    // }
  }

  moveMenuFocus = (e: React.KeyboardEvent, moveUp: boolean) => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      console.log(' -- range -- ', range)
      if (range.collapsed === true) {
        const container = this.getItemElem(range.startContainer, range.startOffset)
        const containerIdx = this.getContainerIdx(container)
        if (containerIdx > -1) {
          this.elems[containerIdx].moveFocus!(moveUp, e)
        }
      }
    }
  }

  onDivKeyDown = (e: React.KeyboardEvent) => {
    console.log(' -- onKeydown -- ', e)

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (range.collapsed === true) {
        const container = this.getItemElem(range.startContainer, range.startOffset)
        if (container != null && container.classList.contains(emptyClsName) === true) {
          const text = container.textContent || ''
          const zwspPos = text.indexOf(zwsp)
          if (e.key === 'Delete' && zwspPos > -1 && text.length === 1) {
            e.preventDefault()
            return
          }
          if (zwspPos < 0) {
            container.textContent = `${zwsp}${text}`
          } else if (zwspPos > 0) {
            const newText = text.replace(zwsp, '')
            container.textContent = `${zwsp}${newText}`
            if (range.startOffset <= zwspPos) {
              this.setCursorPosition(container, range.startOffset + 1)
            }
          }
        }
      }
    }

    const { keyCode, shiftKey } = e.nativeEvent
    if (keyCode === 8) {
      this.checkBackspace(e)
    } else if (keyCode === 9 || (shiftKey === false && (keyCode === 186 || keyCode === 188))) {
      // tab, comma(188) semi-colon(186) - 이메일 선택키로 처리.
      this.checkSelectKey(e)
    } else if (keyCode === 13) {
      this.checkEnter(e)
    } else if (keyCode === 37) {
      // arrowLeft
      this.moveLeft(e)
    } else if (keyCode === 39) {
      // arrowRight
      this.moveRight(e)
    } else if (keyCode === 38) {
      this.moveMenuFocus(e, true)
    } else if (keyCode === 40) {
      this.moveMenuFocus(e, false)
      // escape
      // const dropDown = document.querySelector(`.${this.menuCls}`)
      // if (dropDown != null) {
      //   const menu = dropDown.querySelector('.ant-dropdown-menu')
      //   if (menu.firstElementChild != null) {
      //     menu.firstElementChild.focus()
      //     e.preventDefault()
      //   }
      // }
    }
  }

  onDivClick = (e: MouseEvent) => {
    console.log(' -- onClick -- ', e)
    this.checkClose(e)
  }

  getTypeStr = (txt: string): string => {
    const { innerDomain } = this.props
    const newStr = txt.replace(/\u200B/g, '')
    if (isEmail(newStr) === true) {
      if (innerDomain != null && newStr.endsWith(innerDomain) === true) {
        return innerEmailClsName
      }
      return outerEmailClsName
    }
    return notEmailClsName
  }

  getId = (): string => {
    const id = `idx-${Date.now()}-${this.count}`
    this.count += 1
    return id
  }

  setCursorPosition = (container: Node | null, pos: number, forced: boolean = false) => {
    if (container == null) return
    if (this.hasFocus !== true && forced !== true) return
    const selection = window.getSelection()
    if (selection == null) return
    const maxPos =
      container.nodeType === Node.ELEMENT_NODE
        ? container.childNodes.length
        : (container.textContent?.length ?? 0)
    const safePos = Math.max(0, Math.min(pos, maxPos))
    try {
      selection.setPosition(container, safePos)
    } catch {
      // DOM 변경 중 offset 경계 어긋남은 다음 렌더에 자연 복구되므로 무시
    }
  }

  createNewMailItem = (nodeText?: string, clsName?: string, cursorPos?: number): ElemObj => {
    const { overlayClassName } = this.props
    // const newDiv = document.createElement('div');
    // newDiv.spellcheck = false;
    // newDiv.contenteditable = true;
    // newDiv.className = clsName || emptyClsName;
    // // newDiv.addEventListener('input', this.onDivInput);
    // // newDiv.addEventListener('keydown', this.onDivKeyDown);
    // // newDiv.addEventListener('click', this.onDivClick);
    // if (text != null) {
    //   const textNode = document.createTextNode(text);
    //   newDiv.appendChild(textNode);
    // }

    const id = this.getId()

    const obj: ElemObj = {
      ref: null,
      id,
      needFocus: cursorPos != null,
      clsNames: [clsName || emptyClsName],
      text: nodeText || '',
      cursorPos: cursorPos ?? null,
    }

    const attrs = {
      ref: (ref: HTMLElement | null) => {
        if (ref != null) {
          obj.ref = ref
          ;(obj.ref as any).objInfo = obj
          if (obj.needFocus === true && obj.ref.classList.contains(emailClsName) !== true) {
            this.setCursorPosition(obj.ref.firstChild!, cursorPos!)
            obj.needFocus = false
          }
        }
      },
      key: obj.id,
      objid: obj.id,
    }

    const info = this.parseNodeText(obj.text)

    obj.userInfo = info

    const textForType = info.email || info.name || ''
    const initType = this.getTypeStr(textForType)
    const initEditable = initType === notEmailClsName

    const getEmailFromKey = (key: string): string => {
      let newEmail = key
      const pos = key.indexOf('=')
      if (pos > -1) {
        newEmail = key.substring(0, pos)
      } else {
        newEmail = key
      }
      return newEmail
    }

    const NewDiv = (/* props */) => {
      const [clsNames, setClsNames] = useState(obj.clsNames)
      const [open, setOpen] = useState(false)
      const [type, setType] = useState(initType)
      // const [text, setText] = useState(obj.text)
      const [menu, setMenu] = useState<any[]>([])
      const [editable, setEditable] = useState(initEditable)
      const [selectedKeys, setSelectedKeys] = useState<string[]>([])
      const [userInfo, setUserInfo] = useState(info)

      const setOpenEx = (value: boolean) => {
        console.log(' -- setOpenEx -- ', value)
        setOpen(value)
      }

      const setFocusKey = (key: string) => {
        setSelectedKeys([key])
        const dropDown = document.querySelector(`.${this.menuCls}:not(.ant-dropdown-hidden)`)
        if (dropDown != null) {
          const menuItemElem = dropDown.querySelector(`li.ant-dropdown-menu-item[value="${key}"]`)
          if (menuItemElem != null) {
            menuItemElem.scrollIntoView({ block: 'nearest' })
          }
        }
      }

      const setUserInfoEx = (info: UserInfo) => {
        info.email = getEmailFromKey(info.email || '')
        setUserInfo(info)
        obj.userInfo = info
      }

      obj.setClsNames = (newClsName: string) => {
        setClsNames([newClsName])
        obj.clsNames = [newClsName]
      }

      obj.setFixed = (value: boolean) => {
        setEditable(value === false)
      }
      obj.setMenu = (menu: any[]) => {
        setMenu(menu)
        if (menu.length > 0) {
          const newKey = menu[0].key
          setFocusKey(newKey)
        }
      }
      obj.setMenuOpen = (value: boolean) => {
        if (menu.length > 0) {
          if (selectedKeys.length === 0 || selectedKeys[0] == null) {
            const newKey = menu[0].key
            setFocusKey(newKey)
          } else {
            const idx = menu.findIndex((item) => {
              return item.key === selectedKeys[0]
            })
            if (idx < 0) {
              const newKey = menu[0].key
              setFocusKey(newKey)
            }
          }
        }
        setOpenEx(value)
      }
      obj.closeMenu = () => {
        const ret = open
        setOpenEx(false)
        return ret
      }
      obj.moveFocus = (moveUp: boolean, keyEvent: any) => {
        if (menu.length > 0) {
          keyEvent.preventDefault()
          keyEvent.stopPropagation()
        }
        if (menu.length > 1) {
          let idx = menu.findIndex((item) => {
            return item.key === selectedKeys[0]
          })

          if (idx < 0) {
            idx = 0
          }

          let nextIdx = idx
          if (moveUp === true) {
            nextIdx = (idx + menu.length - 1) % menu.length
          } else {
            nextIdx = (idx + 1) % menu.length
          }

          const newKey = menu[nextIdx].key
          setFocusKey(newKey)
        }
      }
      obj.setText = (txt: string, pos?: number) => {
        obj.ref!.textContent = txt
        if (pos != null) {
          this.setCursorPosition(obj.ref!.firstChild!, pos)
        }
        // setText(txt);
      }
      obj.setPosition = (pos: number) => {
        this.setCursorPosition(obj.ref!.firstChild!, pos)
      }
      obj.checkEmailType = (bySelect: boolean = false, emailTxt?: string, nameTxt?: string) => {
        const email = emailTxt != null ? emailTxt.trim() : obj.ref!.textContent!.trim()
        const typeStr = this.getTypeStr(email)
        setType(typeStr)
        const info: UserInfo = {
          email,
        }
        if (typeStr !== notEmailClsName) {
          if (nameTxt != null && nameTxt.length > 0 && nameTxt.trim() !== email) {
            info.name = nameTxt.trim()
          }
          console.log(' -- checkEmailType - info -- ', info)
          let userInfoEx = { ...info }
          setUserInfoEx(userInfoEx)

          if (bySelect !== true && (info.name == null || info.name.length === 0)) {
            this.searchEmail(email, true)
              .then((ret) => {
                if (ret.length > 0) {
                  const { name, email: value } = ret[0]
                  if (name != null && value != null && name === value) {
                    userInfoEx = { email: value }
                    setUserInfoEx(userInfoEx)
                  } else {
                    userInfoEx = { email: value, name }
                    setUserInfoEx(userInfoEx)
                  }
                }
                this.notifyChanged(userInfoEx)
              })
              .catch(() => {
                this.notifyChanged(userInfoEx)
              })
          } else {
            this.notifyChanged(userInfoEx)
          }
        }
      }
      obj.onSelect = (key: string) => {
        const item = menu.find((item) => {
          return item.key === key
        })
        // setText(zwsp + key)
        const email = getEmailFromKey(item.value)
        this.convertToMailItemOnSelect(obj.ref!, email, item.name)
        setOpenEx(false)
      }
      obj.checkEnter = () => {
        if (open === true && menu.length > 0) {
          const key = selectedKeys[0]
          obj.onSelect!(key)
          return true
        }
        return false
      }

      useEffect(() => {
        console.log(' -- useEffect -- ', obj)
        if (type !== notEmailClsName) {
          let userInfoEx = {}
          const notifyPasteChange = (info) => {
            let pos = this.pasteAddedList.indexOf(info.email)
            if (pos > -1) {
              this.pasteAddedList.splice(pos, 1)
              this.notifyChanged(info)
            }
            pos = this.pasteRemovedList.indexOf(info.email)
            if (pos > -1) {
              this.pasteRemovedList.splice(pos, 1)
              this.notifyChanged(info, true)
            }
          }
          if (
            userInfo.name != null &&
            userInfo.name.length > 0 &&
            userInfo.name.trim() !== userInfo.email!.trim()
          ) {
            userInfoEx = { email: userInfo.email, name: userInfo.name }
            setUserInfoEx(userInfoEx)
            notifyPasteChange(userInfoEx)
          } else {
            userInfoEx = { email: userInfo.email }
            setUserInfoEx(userInfoEx)
            this.searchEmail(userInfo.email!, true)
              .then((ret) => {
                if (ret.length > 0) {
                  const { name, email: value } = ret[0]
                  if (name != null && value != null && name === value) {
                    userInfoEx = { email: value }
                    setUserInfoEx(userInfoEx)
                  } else {
                    userInfoEx = { email: value, name }
                    setUserInfoEx(userInfoEx)
                  }
                }
                notifyPasteChange(userInfoEx)
              })
              .catch(() => {
                notifyPasteChange(userInfoEx)
              })
          }
        }
      }, [])

      const divAttrs: any = {
        ...attrs,
        className: classNames(itemClsName, ...clsNames, type, { fixed: editable === false }),
      }

      if (blockEditable === false && editable === false && !clsNames.includes(emptyClsName)) {
        divAttrs.contentEditable = false
      }

      if (this.menuCls == null) {
        this.menuCls = this.getId()
      }

      const overlayClassName0 =
        overlayClassName != null ? classNames(this.menuCls, overlayClassName) : this.menuCls

      const overlayStyle: React.CSSProperties = {}

      if (obj != null && obj.ref != null) {
        const clientRect = obj.ref.getBoundingClientRect()
        const screenH = document.documentElement.offsetHeight
        const topH = clientRect.top
        const bottomH = clientRect.left

        let maxH = Math.max(topH, screenH - bottomH)
        maxH = Math.round(maxH)
        if (maxH > 100) {
          maxH -= 10
        }

        overlayStyle.maxHeight = `${maxH}px`
      }
      const dropdownAttrs = {
        menu: {
          tabIndex: -1,
          activeKey: selectedKeys.length > 0 ? selectedKeys[0] : null,
          items: menu,
          selectable: true,
          selectedKeys,
          onSelect: ({ key }: { key: string }) => {
            console.log(' -- onSelect -- ', key)
            setFocusKey(key)
          },
          onClick: (e: any) => {
            obj.onSelect!(e.key)
          },
        },
        // popupRender: (menuElem) => {
        //   console.log(' -- menu -- ', menuElem, menu);
        //   return (
        //     <div>
        //       <List
        //         rowKey="key"
        //         itemLayout="horizontal"
        //         dataSource={menu}
        //         renderItem={(item) => (
        //           <List.Item>
        //             {item.label}
        //           </List.Item>
        //         )}
        //       />
        //       {menuElem}
        //     </div>
        //   )
        // },
        // getPopupContainer: () => {
        //   return obj.ref;
        // },
        // destroyOnHidden: true,
        overlayClassName: overlayClassName0,
        // open: open && clsNames.indexOf(emptyClsName) > -1,
        overlayStyle,
        open: open && editable !== false,
      }

      let viewTxt: React.ReactNode = obj.text

      const { name, email } = userInfo
      if (email != null && email.length > 0) {
        divAttrs['data-email'] = email
        viewTxt = email

        if (name != null && name.length > 0) {
          divAttrs['data-name'] = name
          if (type === outerEmailClsName) {
            viewTxt = (
              <React.Fragment>
                {name}
                <span className="email"> &lt;{email}&gt;</span>
              </React.Fragment>
            )
          } else if (type === innerEmailClsName) {
            viewTxt = (
              <React.Fragment>
                {name}
                <span className="email"> {email}</span>
              </React.Fragment>
            )
          }
        }
      }

      return (
        <Dropdown {...dropdownAttrs}>
          <div {...divAttrs}>{viewTxt}</div>
        </Dropdown>
      )
    }

    obj.elem = <NewDiv key={obj.id} />

    return obj
  }

  showMenuFromCurser = (target?: HTMLElement) => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = window.getSelection()!.getRangeAt(0)
      if (range.collapsed === true) {
        let node = range.startContainer as Node | HTMLElement
        console.log(' -- selection 0 -- ', node)

        if (node.nodeType !== Node.ELEMENT_NODE) {
          node = (node as Node).parentElement!
        }

        if (target != null && (node as HTMLElement).contains(target) === true) {
          node = target
        }

        console.log(' -- selection 1 -- ', node)

        let notEmailNode = (node as HTMLElement).closest(
          `.${notEmailClsName}`,
        ) as HTMLElement | null
        if (notEmailNode == null) {
          const items = (node as HTMLElement).querySelectorAll(`.${itemClsName}`)
          if (items.length > 0) {
            const lastItem = items[items.length - 1] as HTMLElement
            if (lastItem.classList.contains(emptyClsName) === true) {
              notEmailNode = lastItem
            }
          }
        }

        console.log(' -- selection 2 -- ', notEmailNode)

        if (notEmailNode != null) {
          this.checkMenu(notEmailNode)
        }
      }
    }
  }

  onSelectionChange = (e: any) => {
    if (this.ref != null) {
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0 && selection.isCollapsed === false) {
        const range = window.getSelection()!.getRangeAt(0)

        // console.log(' -- range -- ', range)

        if (range.collapsed === true) {
          let container: Node | HTMLElement = range.startContainer
          if (container.nodeType !== Node.ELEMENT_NODE) {
            container = container.parentElement!
          }

          if (this.ref === container || this.ref.contains(container as Node) === true) {
            console.log(' -- onSelectionChange -- ', e)
            this.showMenuFromCurser(container as HTMLElement)
          }
        }
      }
    }
  }

  checkTextEmpty = () => {
    let hasText = false
    if (this.ref != null) {
      const text = this.ref.textContent
      if (text != null) {
        const realText = text.replace(/\u200B/g, '')
        hasText = realText.length > 0
      }
    }

    this.setState({
      hasText,
    })
  }

  checkCursorPosition = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && selection.isCollapsed === true) {
      const range = window.getSelection()!.getRangeAt(0)
      const container = this.getItemElem(range.startContainer, range.startOffset)
      if (container != null && container.classList.contains(emptyClsName) === true) {
        const text = container.textContent || ''
        const zwspPos = text.indexOf(zwsp)
        if (range.startContainer === container && zwspPos === range.startOffset) {
          this.setCursorPosition(container, range.startOffset + 1)
        }
      } else if (container == null && this.elems.length > 0) {
        const targetContainer = this.elems[this.elems.length - 1]
        if (targetContainer != null && targetContainer.ref != null) {
          this.setCursorPosition(targetContainer.ref, targetContainer.ref.childNodes.length)
        }
      }
    }
  }

  render() {
    const { hasText } = this.state
    const { placeholder, maxCount, dataTestId } = this.props

    const childList: React.ReactElement[] = []

    const attrs: any = {
      ref: (ref: HTMLElement | null) => {
        this.setRef(ref)
      },
      className: classNames(styles.emailSelectInput, editAreaClsName, { hasText }),
      'data-testid': dataTestId != null ? `${dataTestId}-input` : undefined,
      placeholder: placeholder,
      spellCheck: false,
      contentEditable: maxCount ? this.elems.length < this.props.maxCount * 2 + 1 : true,
      suppressContentEditableWarning: true,
      key: this.key,
      onInput: (e: React.FormEvent<HTMLDivElement>) => {
        const { nativeEvent } = e
        this.onDivInput(nativeEvent as InputEvent)
        this.checkTextEmpty()
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
        this.onDivKeyDown(e)
      },
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        const { nativeEvent } = e
        this.onDivClick(nativeEvent)
      },
      // onKeyUp: (e) => {
      //   console.log(' -- onKeyup -- ', e)
      // },
      // onPaste: (e) => {
      //   console.log(' -- onPaste -- ', e)
      //   this.onPaste(e);
      // },
      onCopy: (e: React.ClipboardEvent<HTMLDivElement>) => {
        console.log(' -- onCopy -- ', e)
        this.processCopy(e, false)
        e.preventDefault()
      },
      onCut: (e: React.ClipboardEvent<HTMLDivElement>) => {
        console.log(' -- onCut -- ', e)
        this.processCopy(e, true)
        e.preventDefault()
      },
      onFocus: (e: React.FocusEvent<HTMLDivElement>) => {
        console.log(' -- onFocus -- ')
        this.hasFocus = true
        this.showMenuFromCurser(e.target)
        this.checkCursorPosition()
      },
      onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
        console.log(' -- onBlur -- ', e)

        this.hasFocus = false

        const menuElem = document.querySelector(`.${this.menuCls}:not(.ant-dropdown-hidden)`)
        if (menuElem != null && menuElem.contains(e.relatedTarget as Node) === true) {
          return
        }

        this.elems.forEach((elem) => {
          if (elem.setMenuOpen != null) {
            elem.setMenuOpen(false)
          }
        })

        // setTimeout(() => {
        //   this.flatten();
        // }, 50);

        this.checkTextEmpty()
      },
    }

    const elems = this.elems.map((elem) => {
      return elem.elem
    })

    childList.push(<div {...attrs}>{elems}</div>)

    const compAttrs = {
      'data-id': this.uniqueId,
      className: classNames(styles.emailSelect),
      'data-testid': dataTestId,
    }

    return <div {...compAttrs}>{childList}</div>
  }
}

const EmailSelectDef = withTranslation()(EmailSelect)

export default EmailSelectDef
